import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_TAM_PRIMES } from '@bloomays-lib/adapter.api-talent';
import { useAnnualTamPrimesStore } from '../stores/annualTamPrimesStore';
import { GetTamPrimesQueryData } from '../types/primes';
import { buildAnnualTamPrimesCacheKey } from '../utils/annualTamPrimesCache.utils';
import { isYearConsultable } from '../utils/clevelPrimesDashboard.utils';

export const useAnnualTamPrimes = (tamEmail: string | undefined, selectedYear: string) => {
  const trimmedTamEmail = tamEmail?.trim() ?? '';
  const cacheKey = useMemo(
    () => (trimmedTamEmail ? buildAnnualTamPrimesCacheKey(trimmedTamEmail, selectedYear) : ''),
    [trimmedTamEmail, selectedYear],
  );
  const cachedEntry = useAnnualTamPrimesStore((state) => (cacheKey ? state.getCache(cacheKey) : null));
  const setCache = useAnnualTamPrimesStore((state) => state.setCache);
  const skip = !trimmedTamEmail || !isYearConsultable(selectedYear);

  const { loading, error, data } = useQuery<GetTamPrimesQueryData>(GET_TAM_PRIMES, {
    skip,
    variables: {
      tamEmail: trimmedTamEmail,
      year: selectedYear,
    },
    context: { clientName: 'api.talents' },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const aggregate = data?.getTamPrimes;
    if (aggregate && cacheKey) {
      setCache(cacheKey, aggregate);
    }
  }, [cacheKey, data?.getTamPrimes, setCache]);

  const networkAggregate = data?.getTamPrimes;
  const aggregate = networkAggregate ?? cachedEntry?.aggregate;
  const isLoading = cachedEntry === null && loading;
  const seniorityMissing = !skip && !loading && !error && data?.getTamPrimes === null;

  return {
    aggregate,
    loading: isLoading,
    error,
    seniorityMissing,
  };
};
